/**
 * Admin — Email Template Editor
 *
 * Split-pane layout (1/3 list, 2/3 detail) matching the wireframe:
 *   Left  — scrollable list grouped: content templates first, then Layout.
 *           Search + status filter at top.
 *   Right — three tabs: Edit / Preview / Versions.
 *
 * Design tokens from styles/theme.ts.  No new dependencies beyond what is
 * already in the project.
 *
 * Super-admin only — route protected at App.tsx level.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { colors } from '../../styles/theme';
import { getApiErrorMessage } from '../../lib/apiError';
import { capitalize } from '../../lib/stringCase';
import { useMovablePopup } from '../../components/MovablePopup';
import { readerLocale } from '../../i18n/language';
import emailTemplatesService, {
  type DraftCreateBody,
  type EmailTemplateDetail,
  type EmailTemplateSummary,
  type EmailTemplateVersionRef,
  type PreviewResponse,
} from '../../services/emailTemplates.service';

// ── Types ─────────────────────────────────────────────────────────────────

type Tab = 'edit' | 'preview' | 'versions';

interface ModalState {
  kind: 'save' | 'publish' | 'discard' | 'version-view' | null;
  versionId?: string;
}

interface EditForm {
  description: string;
  subject_tpl: string;
  html_body: string;
  default_vars_raw: string;  // JSON string
}

// Default preview context pre-filled to match common QMI template variables.
const DEFAULT_CTX = JSON.stringify(
  {
    client_first_name: 'Ana',
    client_last_name: 'Ejemplo',
    recipient_name: 'Ana Ejemplo',
    amount: 5000,
    currency_code: 'MXN',
    portal_url: 'https://dashboard.qmi-investments.com',
    product_name: 'Real Estate Fund I',
    agent_name: 'Staff Member',
    manager_name: 'Manager',
  },
  null,
  2,
);

// ── Small helpers ──────────────────────────────────────────────────────────

function badgeForTemplate(t: EmailTemplateSummary): JSX.Element {
  if (t.is_layout) {
    return (
      <span
        style={{
          fontSize: 11,
          padding: '1px 7px',
          borderRadius: 8,
          background: 'rgba(107,114,128,0.12)',
          color: colors.muted,
          fontWeight: 500,
        }}
      >
        Layout · read-only
      </span>
    );
  }
  if (t.has_pending_draft) {
    return (
      <span
        style={{
          fontSize: 11,
          padding: '1px 7px',
          borderRadius: 8,
          background: 'rgba(180,83,9,0.10)',
          color: colors.warning,
          fontWeight: 600,
        }}
      >
        Draft · v{t.version + 1} pending
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: 11,
        padding: '1px 7px',
        borderRadius: 8,
        background: 'rgba(4,120,87,0.10)',
        color: colors.success,
        fontWeight: 500,
      }}
    >
      Live · v{t.version}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function EmailTemplates(): JSX.Element {
  // ── List state ────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<EmailTemplateSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'draft'>('all');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  // ── Detail state ──────────────────────────────────────────────────
  const [detail, setDetail] = useState<EmailTemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('edit');

  // ── Edit form ─────────────────────────────────────────────────────
  const [form, setForm] = useState<EditForm>({
    description: '',
    subject_tpl: '',
    html_body: '',
    default_vars_raw: '{}',
  });
  const [formDirty, setFormDirty] = useState(false);

  // ── Preview state ─────────────────────────────────────────────────
  const [previewCtx, setPreviewCtx] = useState(DEFAULT_CTX);
  const [previewTarget, setPreviewTarget] = useState<'live' | 'draft'>('live');
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  // ── Versions state ────────────────────────────────────────────────
  const [versions, setVersions] = useState<EmailTemplateVersionRef[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsErr, setVersionsErr] = useState<string | null>(null);

  // ── Modal state ───────────────────────────────────────────────────
  const [modal, setModal] = useState<ModalState>({ kind: null });
  const [modalReason, setModalReason] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  // ── Version viewer ────────────────────────────────────────────────
  const [viewingVersion, setViewingVersion] = useState<EmailTemplateDetail | null>(null);
  const [versionViewErr, setVersionViewErr] = useState<string | null>(null);

  // ── Flash ─────────────────────────────────────────────────────────
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFlash = (msg: string): void => {
    setFlash(msg);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
  };

  // ── Load list ─────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setListLoading(true);
    setListErr(null);
    try {
      setTemplates(await emailTemplatesService.listTemplates());
    } catch (e) {
      setListErr(getApiErrorMessage(e, 'Failed to load templates'));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Load detail ───────────────────────────────────────────────────
  const loadDetail = useCallback(async (code: string) => {
    setDetailLoading(true);
    setDetailErr(null);
    setDetail(null);
    setPreviewResult(null);
    setPreviewErr(null);
    setVersions([]);
    try {
      const d = await emailTemplatesService.getTemplate(code);
      setDetail(d);
      setForm({
        description: d.description ?? '',
        subject_tpl: d.subject_tpl ?? '',
        html_body: d.html_body,
        default_vars_raw: JSON.stringify(d.default_vars, null, 2),
      });
      setFormDirty(false);
      setPreviewTarget(d.has_pending_draft ? 'draft' : 'live');
    } catch (e) {
      setDetailErr(getApiErrorMessage(e, 'Failed to load template detail'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCode) loadDetail(selectedCode);
  }, [selectedCode, loadDetail]);

  // ── Load versions ─────────────────────────────────────────────────
  const loadVersions = useCallback(async (code: string) => {
    setVersionsLoading(true);
    setVersionsErr(null);
    try {
      setVersions(await emailTemplatesService.listVersions(code));
    } catch (e) {
      // Used to be silent, which rendered "No version history yet" for a template
      // that has history: the wrong basis for deciding what to publish.
      setVersions([]);
      setVersionsErr(getApiErrorMessage(e, 'Could not load the version history.'));
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'versions' && selectedCode) loadVersions(selectedCode);
  }, [tab, selectedCode, loadVersions]);

  // ── Filtered list ─────────────────────────────────────────────────
  const filtered = templates.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.code.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q);
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'live' && !t.has_pending_draft) ||
      (statusFilter === 'draft' && t.has_pending_draft);
    return matchSearch && matchStatus;
  });
  const content = filtered.filter((t) => !t.is_layout);
  const layouts = filtered.filter((t) => t.is_layout);

  // ── Save modal helpers ─────────────────────────────────────────────
  const openSaveModal = (): void => {
    setModal({ kind: 'save' });
    setModalReason('');
    setModalPassword('');
    setModalErr(null);
  };

  const openPublishModal = (): void => {
    setModal({ kind: 'publish' });
    setModalReason('');
    setModalPassword('');
    setModalErr(null);
  };

  const openDiscardModal = (): void => {
    setModal({ kind: 'discard' });
    setModalReason('');
    setModalErr(null);
  };

  const closeModal = (): void => {
    setModal({ kind: null });
    setModalReason('');
    setModalPassword('');
    setModalErr(null);
  };

  // ── Save as draft ──────────────────────────────────────────────────
  const handleSaveDraft = async (): Promise<void> => {
    if (!detail || !selectedCode) return;
    if (modalReason.trim().length < 4) {
      setModalErr('Reason must be at least 4 characters.');
      return;
    }
    if (!modalPassword) {
      setModalErr('Password is required.');
      return;
    }

    let parsedVars: Record<string, unknown> = {};
    try {
      parsedVars = JSON.parse(form.default_vars_raw) as Record<string, unknown>;
    } catch {
      setModalErr('Default vars is not valid JSON.');
      return;
    }

    setModalLoading(true);
    setModalErr(null);
    try {
      const body: DraftCreateBody = {
        description: form.description || null,
        subject_tpl: form.subject_tpl || null,
        html_body: form.html_body,
        default_vars: parsedVars,
        reason: modalReason.trim(),
        actor_password: modalPassword,
      };
      await emailTemplatesService.createDraft(selectedCode, body);
      showFlash('Draft saved successfully.');
      closeModal();
      await Promise.all([loadList(), loadDetail(selectedCode)]);
    } catch (e) {
      setModalErr(getApiErrorMessage(e, 'Failed to save draft'));
    } finally {
      setModalLoading(false);
    }
  };

  // ── Publish ────────────────────────────────────────────────────────
  const handlePublish = async (): Promise<void> => {
    if (!detail || !selectedCode || !detail.draft_id) return;
    if (modalReason.trim().length < 4) {
      setModalErr('Reason must be at least 4 characters.');
      return;
    }
    if (!modalPassword) {
      setModalErr('Password is required.');
      return;
    }

    setModalLoading(true);
    setModalErr(null);
    try {
      await emailTemplatesService.publishDraft(selectedCode, {
        draft_id: detail.draft_id,
        reason: modalReason.trim(),
        actor_password: modalPassword,
      });
      showFlash(`v${(detail.version + 1)} published successfully.`);
      closeModal();
      await Promise.all([loadList(), loadDetail(selectedCode)]);
    } catch (e) {
      setModalErr(getApiErrorMessage(e, 'Failed to publish draft'));
    } finally {
      setModalLoading(false);
    }
  };

  // ── Discard draft ──────────────────────────────────────────────────
  const handleDiscard = async (): Promise<void> => {
    if (!detail || !selectedCode || !detail.draft_id) return;
    if (modalReason.trim().length < 4) {
      setModalErr('Reason must be at least 4 characters.');
      return;
    }

    setModalLoading(true);
    setModalErr(null);
    try {
      await emailTemplatesService.discardDraft(detail.draft_id, {
        reason: modalReason.trim(),
      });
      showFlash('Draft discarded.');
      closeModal();
      await Promise.all([loadList(), loadDetail(selectedCode)]);
    } catch (e) {
      setModalErr(getApiErrorMessage(e, 'Failed to discard draft'));
    } finally {
      setModalLoading(false);
    }
  };

  // ── Preview ────────────────────────────────────────────────────────
  const handlePreview = async (): Promise<void> => {
    if (!selectedCode) return;
    setPreviewLoading(true);
    setPreviewErr(null);
    setPreviewResult(null);
    let ctx: Record<string, unknown> = {};
    try {
      ctx = JSON.parse(previewCtx) as Record<string, unknown>;
    } catch {
      setPreviewErr('Context vars is not valid JSON.');
      setPreviewLoading(false);
      return;
    }
    try {
      const result = await emailTemplatesService.preview(selectedCode, {
        ctx_vars: ctx,
        render_target: previewTarget,
      });
      setPreviewResult(result);
    } catch (e) {
      setPreviewErr(getApiErrorMessage(e, 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── View historical version ────────────────────────────────────────
  const handleViewVersion = async (versionId: string): Promise<void> => {
    setViewingVersion(null);
    setVersionViewErr(null);
    setModal({ kind: 'version-view', versionId });
    try {
      const v = await emailTemplatesService.getVersion(versionId);
      setViewingVersion(v);
    } catch (e) {
      // Used to close the modal the instant it opened, with no explanation.
      setVersionViewErr(getApiErrorMessage(e, 'Could not load this version.'));
    }
  };

  // ── Form field helpers ─────────────────────────────────────────────
  const setField =
    (field: keyof EditForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      setFormDirty(true);
    };

  // ── Rendering ─────────────────────────────────────────────────────
  const isReadOnly = detail?.is_layout ?? false;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    color: colors.ink,
    background: isReadOnly ? colors.canvas : colors.surface,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const textareaStyle = (rows: number): React.CSSProperties => ({
    ...inputStyle,
    fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
    resize: 'vertical',
    minHeight: rows * 21,
  });

  return (
    <>
      <PageHeader
        title="Email templates"
        subtitle="Edit transactional email copy. Preview before going live."
      />

      {flash && (
        <div
          className="mb-4 px-4 py-2.5 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        {/* ── LEFT: template list ─────────────────────────────────── */}
        <div
          style={{
            flex: '1 1 300px',
            minWidth: 260,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 200px)',
          }}
        >
          {/* Search + filter bar */}
          <div
            style={{
              padding: '12px 14px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <input
              type="search"
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                padding: '6px 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                fontSize: 12,
                color: colors.ink,
                background: colors.canvas,
                outline: 'none',
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'live' | 'draft')}
              style={{
                padding: '6px 8px',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                fontSize: 12,
                color: colors.ink,
                background: colors.canvas,
                outline: 'none',
              }}
            >
              <option value="all">All</option>
              <option value="live">Live only</option>
              <option value="draft">Draft pending</option>
            </select>
          </div>

          {/* Template list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {listLoading && (
              <p style={{ padding: 16, fontSize: 13, color: colors.muted }}>Loading…</p>
            )}
            {listErr && (
              <p style={{ padding: 16, fontSize: 13, color: colors.danger }}>{listErr}</p>
            )}

            {/* Content templates */}
            {content.map((t) => (
              <button
                key={t.code}
                type="button"
                onClick={() => setSelectedCode(t.code)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  borderBottom: `1px solid ${colors.borderSoft}`,
                  background:
                    selectedCode === t.code
                      ? 'rgba(212,168,75,0.08)'
                      : 'transparent',
                  cursor: 'pointer',
                  borderLeft: selectedCode === t.code
                    ? `3px solid ${colors.gold}`
                    : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 13 }}>
                    {t.has_pending_draft ? '⚠' : '●'}
                  </span>
                  <span
                    style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.ink,
                    }}
                  >
                    {t.code}
                  </span>
                </div>
                {t.description && (
                  <p
                    style={{
                      fontSize: 11,
                      color: colors.muted,
                      margin: '0 0 4px 20px',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.description}
                  </p>
                )}
                <div style={{ marginLeft: 20 }}>{badgeForTemplate(t)}</div>
              </button>
            ))}

            {/* Layout section */}
            {layouts.length > 0 && (
              <>
                <div
                  style={{
                    padding: '8px 14px 4px',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: colors.muted,
                    textTransform: 'uppercase',
                    borderTop: `1px solid ${colors.border}`,
                    background: colors.canvas,
                  }}
                >
                  Layout
                </div>
                {layouts.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    onClick={() => setSelectedCode(t.code)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${colors.borderSoft}`,
                      background:
                        selectedCode === t.code
                          ? 'rgba(107,114,128,0.06)'
                          : 'transparent',
                      cursor: 'pointer',
                      borderLeft: selectedCode === t.code
                        ? `3px solid ${colors.muted}`
                        : '3px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontSize: 13 }}>🔒</span>
                      <span
                        style={{
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: 12,
                          fontWeight: 600,
                          color: colors.inkSoft,
                        }}
                      >
                        {t.code}
                      </span>
                    </div>
                    <div style={{ marginLeft: 20 }}>{badgeForTemplate(t)}</div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── RIGHT: detail pane ─────────────────────────────────── */}
        {/* Wide basis so the two panes wrap (stack) below ~700px (= 300+380+gap)
            instead of crushing the detail pane to an unusable sliver on mobile. */}
        <div style={{ flex: '2 1 380px', minWidth: 0 }}>
          {!selectedCode && (
            <div
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 12,
                padding: 40,
                textAlign: 'center',
                color: colors.muted,
                fontSize: 14,
              }}
            >
              Select a template from the list to edit or preview it.
            </div>
          )}

          {selectedCode && (
            <div
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontFamily: 'ui-monospace, monospace',
                    fontWeight: 700,
                    fontSize: 15,
                    color: colors.ink,
                  }}
                >
                  {selectedCode}
                </span>
                {detail && (
                  <>
                    <span
                      style={{
                        fontSize: 12,
                        color: colors.muted,
                        padding: '2px 8px',
                        background: colors.canvas,
                        borderRadius: 6,
                      }}
                    >
                      v{detail.version}
                    </span>
                    {detail.is_layout && (
                      <span
                        style={{
                          fontSize: 12,
                          color: colors.muted,
                          padding: '2px 8px',
                          background: colors.canvas,
                          borderRadius: 6,
                        }}
                      >
                        read-only
                      </span>
                    )}
                    {detail.updated_by_name && (
                      <span style={{ fontSize: 11, color: colors.muted, marginLeft: 'auto' }}>
                        Updated by {detail.updated_by_name} on{' '}
                        {new Date(detail.updated_at).toLocaleDateString(readerLocale())}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Tabs */}
              <div
                style={{
                  display: 'flex',
                  gap: 0,
                  borderBottom: `1px solid ${colors.border}`,
                  padding: '0 20px',
                }}
              >
                {(['edit', 'preview', 'versions'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    style={{
                      padding: '10px 16px',
                      fontSize: 13,
                      fontWeight: tab === t ? 600 : 400,
                      color: tab === t ? colors.gold : colors.muted,
                      background: 'transparent',
                      border: 'none',
                      borderBottom: tab === t ? `2px solid ${colors.gold}` : '2px solid transparent',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      marginBottom: -1,
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ padding: 20 }}>
                {detailLoading && (
                  <p style={{ color: colors.muted, fontSize: 13 }}>Loading…</p>
                )}
                {detailErr && (
                  <p style={{ color: colors.danger, fontSize: 13 }}>{detailErr}</p>
                )}

                {/* ── EDIT TAB ───────────────────────────────────── */}
                {tab === 'edit' && detail && !detailLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {isReadOnly && (
                      <div
                        style={{
                          padding: '10px 14px',
                          background: 'rgba(107,114,128,0.06)',
                          borderRadius: 8,
                          fontSize: 12,
                          color: colors.muted,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        🔒 Layout templates cannot be edited via the admin UI. Submit a
                        PR with the schema.sql change for mandatory peer review.
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <label htmlFor="emailtpl-description" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
                        Description
                      </label>
                      <input
                        id="emailtpl-description"
                        type="text"
                        value={form.description}
                        onChange={setField('description')}
                        disabled={isReadOnly}
                        style={inputStyle}
                      />
                    </div>

                    {/* Subject template */}
                    {!detail.is_layout && (
                      <div>
                        <label htmlFor="emailtpl-subject" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
                          Subject template
                        </label>
                        <input
                          id="emailtpl-subject"
                          type="text"
                          value={form.subject_tpl}
                          onChange={setField('subject_tpl')}
                          disabled={isReadOnly}
                          placeholder="e.g. Your funding request — {{ portal_name }}"
                          style={inputStyle}
                        />
                      </div>
                    )}

                    {/* HTML body */}
                    <div>
                      <label htmlFor="emailtpl-html-body" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
                        HTML body
                      </label>
                      <textarea
                        id="emailtpl-html-body"
                        value={form.html_body}
                        onChange={setField('html_body')}
                        disabled={isReadOnly}
                        rows={22}
                        style={textareaStyle(22)}
                      />
                    </div>

                    {/* Default vars */}
                    <div>
                      <label htmlFor="emailtpl-default-vars" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
                        Default vars (JSON)
                      </label>
                      <textarea
                        id="emailtpl-default-vars"
                        value={form.default_vars_raw}
                        onChange={setField('default_vars_raw')}
                        disabled={isReadOnly}
                        rows={6}
                        style={textareaStyle(6)}
                      />
                    </div>

                    {/* Draft pending actions */}
                    {detail.has_pending_draft && detail.draft_id && !isReadOnly && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          padding: '12px 14px',
                          background: 'rgba(180,83,9,0.05)',
                          border: `1px solid rgba(180,83,9,0.2)`,
                          borderRadius: 8,
                        }}
                      >
                        <span style={{ fontSize: 13, color: colors.warning, flex: 1 }}>
                          A draft (v{detail.version + 1}) is pending review.
                        </span>
                        <button
                          type="button"
                          onClick={openPublishModal}
                          style={{
                            padding: '6px 14px',
                            background: colors.gold,
                            color: colors.navy,
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Publish v{detail.version + 1} →
                        </button>
                        <button
                          type="button"
                          onClick={openDiscardModal}
                          style={{
                            padding: '6px 14px',
                            background: 'transparent',
                            color: colors.danger,
                            border: `1px solid ${colors.dangerBorder}`,
                            borderRadius: 8,
                            fontSize: 13,
                            cursor: 'pointer',
                          }}
                        >
                          Discard draft
                        </button>
                      </div>
                    )}

                    {/* Save / Cancel row */}
                    {!isReadOnly && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 10,
                          paddingTop: 8,
                          borderTop: `1px solid ${colors.border}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (detail) {
                              setForm({
                                description: detail.description ?? '',
                                subject_tpl: detail.subject_tpl ?? '',
                                html_body: detail.html_body,
                                default_vars_raw: JSON.stringify(detail.default_vars, null, 2),
                              });
                              setFormDirty(false);
                            }
                          }}
                          style={{
                            padding: '8px 18px',
                            background: 'transparent',
                            color: colors.muted,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            fontSize: 13,
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={openSaveModal}
                          disabled={!formDirty}
                          style={{
                            padding: '8px 18px',
                            background: formDirty ? colors.gold : colors.disabledBg,
                            color: formDirty ? colors.navy : colors.disabledText,
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: formDirty ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {detail.has_pending_draft ? 'Update draft' : 'Save as draft'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── PREVIEW TAB ────────────────────────────────── */}
                {tab === 'preview' && detail && !detailLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label htmlFor="emailtpl-context-vars" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
                        Context vars (JSON)
                      </label>
                      <textarea
                        id="emailtpl-context-vars"
                        value={previewCtx}
                        onChange={(e) => setPreviewCtx(e.target.value)}
                        rows={10}
                        style={textareaStyle(10)}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel }}>
                        Render target:
                      </span>
                      {(['live', 'draft'] as const).map((t) => (
                        <label
                          key={t}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 13,
                            color: t === 'draft' && !detail.has_pending_draft ? colors.disabledText : colors.ink,
                            cursor: t === 'draft' && !detail.has_pending_draft ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name="preview_target"
                            value={t}
                            checked={previewTarget === t}
                            disabled={t === 'draft' && !detail.has_pending_draft}
                            onChange={() => setPreviewTarget(t)}
                          />
                          {capitalize(t)}
                        </label>
                      ))}

                      <button
                        type="button"
                        onClick={() => handlePreview()}
                        disabled={previewLoading}
                        style={{
                          marginLeft: 'auto',
                          padding: '8px 18px',
                          background: colors.gold,
                          color: colors.navy,
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: previewLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {previewLoading ? 'Rendering…' : 'Render'}
                      </button>
                    </div>

                    {previewErr && (
                      <div
                        style={{
                          padding: '10px 14px',
                          background: colors.dangerBg,
                          border: `1px solid ${colors.dangerBorder}`,
                          borderRadius: 8,
                          color: colors.danger,
                          fontSize: 13,
                        }}
                      >
                        {previewErr}
                      </div>
                    )}

                    {previewResult && (
                      <>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: colors.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Subject
                          </div>
                          <div
                            style={{
                              padding: '8px 12px',
                              background: colors.canvas,
                              border: `1px solid ${colors.border}`,
                              borderRadius: 8,
                              fontSize: 13,
                              color: colors.ink,
                            }}
                          >
                            {previewResult.subject}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: colors.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            HTML preview
                          </div>
                          {/* Empty sandbox — most restrictive; no scripts, no navigation */}
                          <iframe
                            sandbox=""
                            title="Email HTML preview"
                            srcDoc={previewResult.html_body}
                            style={{
                              width: '100%',
                              height: 500,
                              border: `1px solid ${colors.border}`,
                              borderRadius: 8,
                              background: '#fff',
                            }}
                          />
                        </div>
                        {previewResult.warnings.length > 0 && (
                          <div
                            style={{
                              padding: '10px 14px',
                              background: colors.warningBg,
                              border: `1px solid rgba(180,83,9,0.2)`,
                              borderRadius: 8,
                              fontSize: 12,
                              color: colors.warning,
                            }}
                          >
                            {previewResult.warnings.map((w) => (
                              <div key={w}>{w}</div>
                            ))}
                          </div>
                        )}
                        <p style={{ fontSize: 11, color: colors.muted }}>
                          Assets (cid:…) shown as broken — production renders them inline via SMTP multipart/related.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* ── VERSIONS TAB ───────────────────────────────── */}
                {tab === 'versions' && (
                  <div>
                    {versionsLoading && (
                      <p style={{ fontSize: 13, color: colors.muted }}>Loading versions…</p>
                    )}
                    {!versionsLoading && versions.length === 0 && (
                      <VersionsEmptyState error={versionsErr} />
                    )}
                    {!versionsLoading && versions.length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            {['Version', 'Saved at', 'By', 'Status'].map((h) => (
                              <th
                                key={h}
                                style={{
                                  textAlign: 'left',
                                  padding: '8px 12px',
                                  borderBottom: `2px solid ${colors.border}`,
                                  fontWeight: 600,
                                  fontSize: 11,
                                  color: colors.muted,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {versions.map((v) => (
                            <tr
                              key={v.id}
                              onClick={() => handleViewVersion(v.id)}
                              style={{
                                cursor: 'pointer',
                                borderBottom: `1px solid ${colors.borderSoft}`,
                              }}
                            >
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600 }}>
                                v{v.version}
                              </td>
                              <td style={{ padding: '8px 12px', color: colors.muted }}>
                                {new Date(v.updated_at).toLocaleString(readerLocale())}
                              </td>
                              <td style={{ padding: '8px 12px', color: colors.muted }}>
                                {v.updated_by_name ?? '—'}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {v.is_active && (
                                  <span style={{ color: colors.success, fontWeight: 600, fontSize: 12 }}>Live</span>
                                )}
                                {v.is_draft && (
                                  <span style={{ color: colors.warning, fontWeight: 600, fontSize: 12 }}>Draft</span>
                                )}
                                {!v.is_active && !v.is_draft && (
                                  <span style={{ color: colors.muted, fontSize: 12 }}>Archived</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <p style={{ fontSize: 11, color: colors.muted, marginTop: 12 }}>
                      No rollback button — to reuse an old version, click it to view, copy the subject and HTML, paste into the Edit tab, then save and publish.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── MODAL: Save as draft ──────────────────────────────────── */}
      {modal.kind === 'save' && (
        <ModalOverlay onClose={closeModal}>
          <ModalBox
            title={detail?.has_pending_draft ? 'Update draft' : 'Save as draft'}
            onClose={closeModal}
          >
            <ModalBody
              subtitle={`Saving ${selectedCode ?? ''} as a draft. The live version is unchanged until you publish.`}
              reason={modalReason}
              setReason={setModalReason}
              password={modalPassword}
              setPassword={setModalPassword}
              err={modalErr}
              loading={modalLoading}
              onCancel={closeModal}
              onConfirm={() => handleSaveDraft()}
              confirmLabel="Save draft"
            />
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── MODAL: Publish ────────────────────────────────────────── */}
      {modal.kind === 'publish' && detail && (
        <ModalOverlay onClose={closeModal}>
          <ModalBox
            title={`Publish v${detail.version + 1} of ${selectedCode}?`}
            onClose={closeModal}
          >
            <ModalBody
              subtitle={`This will replace the current live version (v${detail.version}). Emails sent after this point will use v${detail.version + 1}.`}
              reason={modalReason}
              setReason={setModalReason}
              password={modalPassword}
              setPassword={setModalPassword}
              err={modalErr}
              loading={modalLoading}
              onCancel={closeModal}
              onConfirm={() => handlePublish()}
              confirmLabel="Publish"
              confirmStyle={{ background: colors.gold, color: colors.navy }}
            />
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── MODAL: Discard draft ──────────────────────────────────── */}
      {modal.kind === 'discard' && (
        <ModalOverlay onClose={closeModal}>
          <ModalBox title="Discard draft?" onClose={closeModal}>
            <ModalBody
              subtitle="This will permanently delete the draft. The live version is unchanged."
              reason={modalReason}
              setReason={setModalReason}
              err={modalErr}
              loading={modalLoading}
              onCancel={closeModal}
              onConfirm={() => handleDiscard()}
              confirmLabel="Discard"
              confirmStyle={{ background: colors.danger, color: '#fff' }}
              // No password required for discard
              password={undefined}
              setPassword={undefined}
            />
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── MODAL: Version viewer ─────────────────────────────────── */}
      {modal.kind === 'version-view' && (
        <ModalOverlay onClose={closeModal}>
          <ModalBox
            title={`Version ${viewingVersion?.version ?? '…'} of ${selectedCode}`}
            onClose={closeModal}
            wide
          >
            <div style={{ overflowY: 'auto', maxHeight: '70vh' }}>
              {!viewingVersion && <VersionLoadingState error={versionViewErr} />}
              {viewingVersion && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: colors.muted, marginBottom: 2 }}>Subject</div>
                    <div
                      style={{
                        padding: '6px 10px',
                        background: colors.canvas,
                        borderRadius: 6,
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      {viewingVersion.subject_tpl ?? '(no subject)'}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: colors.muted, marginBottom: 4 }}>HTML body</div>
                  <iframe
                    sandbox=""
                    title={`Version ${viewingVersion.version} preview`}
                    srcDoc={viewingVersion.html_body}
                    style={{
                      width: '100%',
                      height: 420,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  />
                  <p style={{ fontSize: 11, color: colors.muted, marginTop: 8 }}>
                    Read-only view. To reuse: copy subject + HTML, paste into Edit tab, save &amp; publish.
                  </p>
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  padding: '8px 18px',
                  background: colors.canvas,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
}

// ── Shared modal sub-components ────────────────────────────────────────────

function ModalOverlay({
  children,
  onClose,
}: Readonly<{
  children: React.ReactNode;
  onClose: () => void;
}>): JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          border: 'none',
          padding: 0,
          cursor: 'default',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

function ModalBox({
  title,
  children,
  onClose,
  wide = false,
}: Readonly<{
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  return (
    <div
      ref={popup.panelRef}
      style={{
        ...popup.panelStyle,
        background: colors.surface,
        borderRadius: 16,
        padding: 28,
        width: wide ? 700 : 480,
        maxWidth: '95vw',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
      }}
    >
      {popup.bar}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.ink }}>{title}</h3>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 20,
            color: colors.muted,
            cursor: 'pointer',
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

function ModalBody({
  subtitle,
  reason,
  setReason,
  password,
  setPassword,
  err,
  loading,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmStyle,
}: Readonly<{
  subtitle: string;
  reason: string;
  setReason: (v: string) => void;
  password: string | undefined;
  setPassword: ((v: string) => void) | undefined;
  err: string | null;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmStyle?: React.CSSProperties;
}>): JSX.Element {
  return (
    <div>
      <p style={{ fontSize: 13, color: colors.muted, marginBottom: 16, lineHeight: 1.6 }}>
        {subtitle}
      </p>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="emailtpl-modal-reason" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
          Reason <span style={{ color: colors.danger }}>*</span>
        </label>
        <textarea
          id="emailtpl-modal-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Describe why this change is being made…"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            fontSize: 13,
            color: colors.ink,
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {password !== undefined && setPassword !== undefined && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="emailtpl-modal-password" style={{ fontSize: 12, fontWeight: 600, color: colors.textLabel, display: 'block', marginBottom: 4 }}>
            Your password <span style={{ color: colors.danger }}>*</span>
          </label>
          <input
            id="emailtpl-modal-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              fontSize: 13,
              color: colors.ink,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {err && (
        <div
          style={{
            padding: '8px 12px',
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
            borderRadius: 8,
            color: colors.danger,
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            padding: '8px 18px',
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            fontSize: 13,
            cursor: loading ? 'not-allowed' : 'pointer',
            color: colors.muted,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          style={{
            padding: '8px 18px',
            background: colors.gold,
            color: colors.navy,
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            ...confirmStyle,
          }}
        >
          {loading ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

/** Versions tab when the list is empty: says whether it is genuinely empty or
 *  could not be read, because the two lead to different publishing decisions. */
function VersionsEmptyState({ error }: Readonly<{ error: string | null }>): JSX.Element {
  if (error) {
    return <p role="alert" style={{ fontSize: 13, color: colors.danger }}>{error}</p>;
  }
  return <p style={{ fontSize: 13, color: colors.muted }}>No version history yet.</p>;
}

/** Version viewer body before the version arrives: loading, or why it will not. */
function VersionLoadingState({ error }: Readonly<{ error: string | null }>): JSX.Element {
  if (error) {
    return (
      <p role="alert" style={{ fontSize: 13, color: colors.danger, padding: 16 }}>{error}</p>
    );
  }
  return <p style={{ fontSize: 13, color: colors.muted, padding: 16 }}>Loading…</p>;
}
